import { Switch, Text, Tooltip } from '@mantine/core';
import { capitalize, join, last, replace, split, startsWith } from 'lodash/fp';
import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import {
  INCIDENTS_API_URL,
  PartnerIncidentType,
  useIsChannelPartner,
  usePaginatedIncidents,
} from '@portals/api/partners';
import { DashboardContentLayout } from '@portals/framework';
import {
  AvatarCell,
  BadgeCell,
  DateCell,
  PaginatedTable,
} from '@portals/table';
import {
  TableColumn,
  TableColumnFormatter,
  TableFilterTypeEnum,
} from '@portals/types';
import { INCIDENT_TYPE, PRIORITY_OPTIONS } from '@portals/utils';

import { IncidentDetailsPanel } from './IncidentDetailsPanel';

const priorityFormatter: TableColumnFormatter<PartnerIncidentType> = (
  cell,
  { priority }
) => {
  const { color, title } = INCIDENT_TYPE[priority - 1];

  return <BadgeCell color={color} label={title} />;
};

const spaceFormatter: TableColumnFormatter<PartnerIncidentType> = (
  _,
  { space_path }
) => {
  const spaceName = last(split('/', space_path));
  const fullPath = join(' / ', split('/', space_path));

  return (
    <Tooltip label={fullPath}>
      <Text inherit>{spaceName}</Text>
    </Tooltip>
  );
};

const IncidentsList: FC = () => {
  const isChannelPartner = useIsChannelPartner();

  const [isLabIncluded, setIsLabIncluded] = useLocalStorage(
    'incidents.isLabIncluded',
    !isChannelPartner
  );

  const tableColumns: Array<TableColumn<PartnerIncidentType>> = [
    {
      dataField: 'title',
      text: 'Title',
      sort: true,
      isSticky: true,
      filter: { type: TableFilterTypeEnum.Text },
      formatter: (_, { title, id }) => (
        <Link to={`/incidents/${id}`}>{title}</Link>
      ),
    },
    {
      dataField: 'priority',
      text: 'Priority',
      sort: true,
      filter: {
        type: TableFilterTypeEnum.Select,
        options: PRIORITY_OPTIONS,
        placeholder: 'Priority...',
      },
      formatter: priorityFormatter,
    },
    {
      dataField: 'device_name',
      text: 'Device',
      sort: true,
      filter: {
        type: TableFilterTypeEnum.Text,
      },
    },
    {
      dataField: 'device_model',
      text: 'Model',
      sort: true,
      filter: {
        type: TableFilterTypeEnum.Text,
      },
      formatter: (_, { device_model_name }) => device_model_name,
    },
    {
      dataField: 'organization_display_name',
      text: 'Vendor',
      sort: true,
      filter: { type: TableFilterTypeEnum.Text },
      formatter: (_, { org_display_name, org_logo_url }) => (
        <AvatarCell label={org_display_name} src={org_logo_url} />
      ),
    },
    {
      dataField: 'space_tree_path_name',
      text: 'Space',
      sort: true,
      filter: { type: TableFilterTypeEnum.Text },
      formatter: spaceFormatter,
    },
    {
      dataField: 'issue',
      text: 'Trigger',
      sort: true,
      filter: { type: TableFilterTypeEnum.Text },
      formatter: (_, { issue }) => {
        const isRule = startsWith('rule_', issue);

        if (isRule && isChannelPartner) {
          return 'Rule';
        }

        if (isRule) {
          return <Link to={`/rules/${replace('rule_', '', issue)}`}>Rule</Link>;
        }

        if (issue === 'user') {
          return 'Manual';
        }

        return capitalize(issue);
      },
    },
    {
      dataField: 'created_at',
      text: 'Created',
      sort: true,
      filter: { type: TableFilterTypeEnum.Date },
      formatter: (cell, { created_at }) => <DateCell date={created_at} />,
    },
  ];

  return (
    <DashboardContentLayout pageTitle="Incidents">
      <PaginatedTable<PartnerIncidentType>
        keyField="id"
        name="partners.incidents"
        dataHook={usePaginatedIncidents}
        dataHookUrl={`${INCIDENTS_API_URL}/?q[organization_lab_eq]=${isLabIncluded}`}
        columns={tableColumns}
        noDataIndication={{ title: 'No Incidents' }}
        defaultSortBy={[{ id: 'priority', desc: false }]}
        additionalActions={() => (
          <Switch
            label="Include lab customers"
            checked={isLabIncluded}
            onChange={(e) => setIsLabIncluded(e.target.checked)}
          />
        )}
        detailsPanel={{
          type: 'page',
          renderer: ({ row, onClose }) => (
            <IncidentDetailsPanel
              incident={row.original}
              onClosePanel={onClose}
            />
          ),
        }}
      />
    </DashboardContentLayout>
  );
};

export default IncidentsList;
